/* ==========================================================================
   WashWax homepage behaviour
   - Product tabs + row arrows (<wwh-tabs>)
   - Quick add to cart on any [data-wwh-add] button inside a .wwh section
   Uses the Enterprise theme's cart drawer and cart icon when present.
   ========================================================================== */
(() => {
  'use strict';
  if (window.__wwhHomeLoaded) return;
  window.__wwhHomeLoaded = true;

  const routes = (window.theme && window.theme.routes) || { cart: '/cart', cartAdd: '/cart/add' };

  /**
   * Adds one or more variants to the cart and refreshes the theme cart UI.
   * @param {number|string} variantId Variant id, or several separated by commas.
   * @returns {Promise<object>} Shopify's response with rendered sections.
   */
  async function addToCart(variantId) {
    const drawer = document.querySelector('cart-drawer');
    const sections = ['cart-icon-bubble'];
    const drawerSection = drawer && drawer.closest('.shopify-section');
    if (drawerSection) sections.push(drawerSection.id.replace('shopify-section-', ''));

    const response = await fetch(`${routes.cartAdd}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: String(variantId).split(',').filter(Boolean).map((id) => ({ id: Number(id), quantity: 1 })),
        sections: sections.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || ((window.wwT && wwT.cart_error) || 'That could not be added to your cart.'));
    }

    const ProductForm = customElements.get('product-form');
    if (ProductForm && typeof ProductForm.updateCartIcon === 'function' && data.sections) {
      try { ProductForm.updateCartIcon(data); } catch (e) { /* theme API changed */ }
    }
    if (drawer && typeof drawer.renderContents === 'function' && data.sections) {
      drawer.renderContents(data, true);
    } else {
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
    }
    document.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true, detail: { source: 'wwh' } }));
    return data;
  }

  // Quick add, delegated so it works for every section and after editor reloads.
  document.addEventListener('click', async (evt) => {
    const btn = evt.target.closest('.wwh [data-wwh-add]');
    if (!btn || btn.classList.contains('is-loading')) return;
    evt.preventDefault();
    const scope = btn.closest('[data-wwh-card]') || btn.closest('.wwh');
    const errorEl = scope ? scope.querySelector('[data-wwh-error]') : null;
    if (errorEl) errorEl.hidden = true;

    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    try {
      await addToCart(btn.dataset.wwhAdd);
      btn.classList.add('is-added');
      const label = btn.querySelector('span');
      const original = label ? label.textContent : '';
      if (label) label.textContent = (window.wwT && wwT.added) || 'Added';
      setTimeout(() => {
        btn.classList.remove('is-added');
        if (label) label.textContent = original;
      }, 1600);
    } catch (error) {
      if (errorEl) {
        errorEl.textContent = error.message;
        errorEl.hidden = false;
      }
    } finally {
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-busy');
    }
  });

  // Size tiles on product cards: update size name, price, sale tag, photo, links and the Add to cart variant.
  document.addEventListener('change', (evt) => {
    const input = evt.target.closest('.wwh [data-wwh-sizes] input[type="radio"]');
    if (!input) return;
    const card = input.closest('[data-wwh-card]');
    if (!card) return;
    const d = input.dataset;

    const name = card.querySelector('[data-wwh-size-name]');
    if (name && d.label) name.textContent = d.label;

    const price = card.querySelector('[data-wwh-price]');
    if (price) { if (window.wwMoneyHTML) price.innerHTML = window.wwMoneyHTML(d.price || ''); else price.textContent = d.price || ''; }
    const was = card.querySelector('[data-wwh-was]');
    if (was) { was.textContent = d.compare || ''; was.hidden = !d.compare; }

    // Automatic "Save X%" tag follows the selected size.
    const tag = card.querySelector('.wwh-tag--sale[data-wwh-tag]');
    if (tag) {
      const text = tag.querySelector('[data-wwh-tag-text]');
      if (d.save) {
        if (text && tag.dataset.wwhTagAuto !== undefined) text.textContent = ((window.wwT && wwT.save_pct) || 'Save {pct}%').replace('{pct}', d.save);
        tag.hidden = false;
      } else if (text && /^Save /.test(text.textContent)) {
        tag.hidden = true;
      }
    }

    const add = card.querySelector('[data-wwh-add]');
    if (add) {
      add.dataset.wwhAdd = input.value;
      add.disabled = input.disabled;
      const label = add.querySelector('span');
      if (label) {
        if (!add.dataset.wwhLabel) add.dataset.wwhLabel = label.textContent;
        label.textContent = input.disabled ? ((window.wwT && wwT.sold_out) || 'Sold out') : add.dataset.wwhLabel;
      }
    }
    if (d.url) {
      card.querySelectorAll('[data-wwh-more], [data-wwh-link], .wwh-pcard__media').forEach((el) => { el.href = d.url; });
    }

    const media = card.querySelector('.wwh-pcard__media');
    const img = media && !media.hasAttribute('data-keep-img') ? media.querySelector('img') : null;
    if (img && d.img) {
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = d.img;
    }

    const err = card.querySelector('[data-wwh-error]');
    if (err) err.hidden = true;
  });

  // Jump bar: highlight the shelf in view and keep its link visible.
  function initJump(root) {
    root.querySelectorAll('[data-wwh-jump]').forEach((nav) => {
      if (nav.dataset.wwhReady) return;
      nav.dataset.wwhReady = '1';

      // Lift the bar out of its section so it stays pinned across every product grid below it.
      const host = nav.closest('.shopify-section');
      if (host && host.parentNode && !window.Shopify?.designMode) host.parentNode.insertBefore(nav, host);

      // One link per shelf on the page, in page order (shelves can live in several sections).
      const track = nav.querySelector('.wwh-jump__track');
      const shelves = Array.from(document.querySelectorAll('.wwh-shelf[data-wwh-nav]'));
      if (track && shelves.length) {
        track.innerHTML = '';
        shelves.forEach((sh) => {
          const a = document.createElement('a');
          a.className = 'wwh-jump__link';
          a.href = `#${sh.id}`;
          a.textContent = sh.dataset.wwhNav;
          track.appendChild(a);
        });
      }
      const links = Array.from(nav.querySelectorAll('a[href^="#"]'));
      const map = new Map();
      links.forEach((a) => {
        const target = document.querySelector(a.getAttribute('href'));
        if (target) map.set(target, a);
      });
      if (!map.size || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          links.forEach((a) => a.classList.remove('is-active'));
          const a = map.get(entry.target);
          if (!a) return;
          a.classList.add('is-active');
          const bar = a.parentElement;
          if (bar && bar.scrollWidth > bar.clientWidth) {
            bar.scrollTo({ left: a.offsetLeft - 16, behavior: 'smooth' });
          }
        });
      }, { rootMargin: '-35% 0px -60% 0px' });
      map.forEach((a, el) => io.observe(el));
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initJump(document));
  else initJump(document);
  document.addEventListener('shopify:section:load', (evt) => initJump(evt.target));

  /* <wwh-tabs> ------------------------------------------------------------- */
  class WwhTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[data-wwh-panel]'));

      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (evt) => {
          if (evt.key !== 'ArrowRight' && evt.key !== 'ArrowLeft') return;
          evt.preventDefault();
          const next = (i + (evt.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
          this.select(next);
          this.tabs[next].focus();
        });
      });

      const prev = this.querySelector('[data-wwh-prev]');
      const next = this.querySelector('[data-wwh-next]');
      if (prev) prev.addEventListener('click', () => this.scroll(-1));
      if (next) next.addEventListener('click', () => this.scroll(1));
    }

    /** @param {number} index Tab to show. */
    select(index) {
      this.tabs.forEach((tab, i) => {
        const on = i === index;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
      });
      this.panels.forEach((panel, i) => {
        panel.hidden = i !== index;
        if (i === index) panel.scrollLeft = 0;
      });
    }

    /** @param {number} dir -1 for left, 1 for right. */
    scroll(dir) {
      const panel = this.panels.find((p) => !p.hidden);
      if (!panel) return;
      panel.scrollBy({ left: dir * Math.max(panel.clientWidth * 0.8, 240), behavior: 'smooth' });
    }
  }

  if (!customElements.get('wwh-tabs')) customElements.define('wwh-tabs', WwhTabs);
})();
